/**
 * Search History Hook
 * Manages localStorage-based search history with network awareness
 */

import { useState, useEffect } from 'react'
import { configService } from '@/config'
import { log } from '@/utils/logger'

export interface SearchHistoryItem {
  query: string
  type: string // 'transaction', 'account', 'validator', etc.
  timestamp: number
  network: string
}

const MAX_HISTORY_ITEMS = 10

/**
 * Get the localStorage key for the current network
 */
function getStorageKey(): string {
  const network = configService.getCurrentNetwork()
  return `x1-search-history-${network}`
}

/**
 * Load search history from localStorage for current network
 */
function loadHistory(): SearchHistoryItem[] {
  try {
    const key = getStorageKey()
    const stored = localStorage.getItem(key)
    if (stored) {
      const parsed = JSON.parse(stored)
      // Validate that items have required properties
      if (Array.isArray(parsed)) {
        return parsed.filter(item =>
          item.query &&
          item.type &&
          item.timestamp &&
          item.network
        )
      }
    }
  } catch (error) {
    log.warn('Failed to load search history:', error)
  }
  return []
}

/**
 * Save search history to localStorage for current network
 */
function saveHistory(history: SearchHistoryItem[]): void {
  try {
    const key = getStorageKey()
    localStorage.setItem(key, JSON.stringify(history))
  } catch (error) {
    log.warn('Failed to save search history:', error)
  }
}

/**
 * Hook for managing search history
 */
export function useSearchHistory() {
  const [history, setHistory] = useState<SearchHistoryItem[]>([])
  const currentNetwork = configService.getCurrentNetwork()

  // Load history on mount and when network changes
  useEffect(() => {
    setHistory(loadHistory())
  }, [currentNetwork])

  /**
   * Add a new search to history
   */
  const addToHistory = (query: string, type: string) => {
    if (!query.trim()) return

    const newItem: SearchHistoryItem = {
      query: query.trim(),
      type,
      timestamp: Date.now(),
      network: currentNetwork
    }

    setHistory(prevHistory => {
      // Remove duplicates (same query)
      const filtered = prevHistory.filter(item =>
        item.query.toLowerCase() !== newItem.query.toLowerCase()
      )

      // Add new item at the beginning
      const updated = [newItem, ...filtered].slice(0, MAX_HISTORY_ITEMS)

      // Save to localStorage
      saveHistory(updated)

      return updated
    })
  }

  /**
   * Remove a specific item from history
   */
  const removeFromHistory = (query: string) => {
    setHistory(prevHistory => {
      const updated = prevHistory.filter(item =>
        item.query.toLowerCase() !== query.toLowerCase()
      )
      saveHistory(updated)
      return updated
    })
  }

  /**
   * Clear all search history for current network
   */
  const clearHistory = () => {
    setHistory([])
    try {
      const key = getStorageKey()
      localStorage.removeItem(key)
    } catch (error) {
      log.warn('Failed to clear search history:', error)
    }
  }

  /**
   * Get recent searches (last N items)
   */
  const getRecentSearches = (limit: number = 5): SearchHistoryItem[] => {
    return history.slice(0, limit)
  }

  return {
    history,
    addToHistory,
    removeFromHistory,
    clearHistory,
    getRecentSearches
  }
}
